import type { ContextType, PropsWithChildren } from 'react';
import { EMPTY, of, Subject } from 'rxjs';

import { BlotterTableContext, type BlotterTableContextProps } from '../components/BlotterTable/BlotterTableContext';
import { AVA_PING, WebSocketStatus } from '../tokens';
import { envConfig } from '../utils/envConfig';
import { PortalContextProvider } from './PortalContextProvider';
import { WebSocketContext, type WebSocketContextProps } from './WebSocketClientProvider';

import { ThemeContext } from 'styled-components';
import { CurrenciesContext, type CurrenciesContextProps } from '../contexts/CurrenciesContext';
import { EndpointsContext, type EndpointsContextProps } from '../contexts/EndpointsContext';
import { HomeCurrencyContext, type HomeCurrencyContextProps } from '../contexts/HomeCurrencyContext';
import { DEFAULT_LOCALE, intlDefaultFormatter } from '../contexts/IntlContext';
import { MarketsContext, type MarketsContextProps } from '../contexts/MarketsContext';
import { GET_MIXPANEL_MOCK, MixpanelContext } from '../contexts/MixpanelContext';
import { MixpanelSourceContext } from '../contexts/MixpanelSourceContext';
import { SecuritiesContext, type SecuritiesContextProps } from '../contexts/SecuritiesContext';
import { StrategiesContext, type StrategiesContextProps } from '../contexts/StrategiesContext';
import { UserContext, type UserContextProps } from '../contexts/UserContext';
import { AppwideDrawerProviderWithLocalGuideState } from '../providers/AppwideDrawerProvider';
import { DarkTheme } from '../styles';
import {
  OrderStrategy,
  SubProductTypeEnum,
  type JSON_OrderStrategy,
  type OTCFixedRateLoanProductTemplate,
  type OTCFloatingRateLoanProductTemplate,
  type SubscriptionResponse,
} from '../types';
import type { RequestStream } from '../types/RequestStream';
import type { Security } from '../types/Security';
import { formattedDateForSubscription } from '../utils/date';
import { EMPTY_OBJECT } from '../utils/empty';
import { GlobalDialogProvider } from './GlobalDialogProvider';
import { IntlProvider } from './IntlProvider';
import { isActiveSecurity } from './SecuritiesProvider/SecuritiesProvider';
import type { IWebSocketClient } from './WebSocketClient';
import { Subscription, type SubscriptionCallback } from './subscription';

interface CreateMockWebsocketContextParams {
  /**
   * An optional callback you can implement if you need more granular control over the mock sendMessage functionality.
   *
   * The default functionality this mock websocket employs is to just match messages <-> streams using stream.name === json.type.
   * However, this does not handle the case where you might have several streams registered within just one type.
   *
   * In these cases, this callback helps you. The callback gives you full control over to which stream a message should go.
   * On every message you try to send to sendMessage, this callback function will be called for every registered stream.
   *
   * The most default implementation of this callback would be `return json.type === stream.name`. On top of this you would add your
   * bespoke if-statements to split up message sending for specific types where you know you have several streams open.
   * @param json the message the websocket client is trying to send
   * @param stream the stream to consider for sending this message to
   * @returns true if the message should be sent on this stream, false if it should not be sent on this stream
   */
  shouldSendToThisStream?: (json: SubscriptionResponse<any>, stream: RequestStream) => boolean;
  /** Called when a subscription is done. Provides you with the subscription instance, which you can use to return a message directly */
  onSubscription?: (sub: Subscription<any>) => void;
}

/**
 * Create a mock websocket context for testing.
 * @param shouldSendToThisStream - A function that determines if a message should be sent to a specific stream.
 * @param onSubscription - A function that is called when a subscription is done. Provides you with the subscription instance, which you can use to return a message directly
 * @returns A mock websocket context.
 */
export const createMockWebsocketContext = ({
  shouldSendToThisStream,
  onSubscription,
}: CreateMockWebsocketContextParams = EMPTY_OBJECT) => {
  return (
    import.meta.env.VITE_IS_TEST_ENVIRONMENT
      ? ({
          client: {
            subscriptions: [] as Subscription<any>[],
            publications: new Map<number, (err: any) => void>(),
            isConnected: true,
            sessionId: 'MOCK_SESSION',
            connect: () => {},
            reqid: 0,
            registerSubscription(address, streams, callback, _) {
              const subscription = new Subscription<any>({
                address,
                streams,
                callback,
                reqid: this.reqid,
              });
              this.reqid++;
              this.subscriptions.push(subscription);
              setTimeout(() => onSubscription?.(subscription), 0); // don't run right away, not realistic and causes issues
              return () => this.unregisterSubscription(address);
            },
            updateSubscription(address: string, streams: RequestStream[], callback: SubscriptionCallback<any>) {
              const subscription = new Subscription<any>({
                address,
                streams,
                callback,
                reqid: this.reqid,
              });
              this.reqid++;
              this.subscriptions = [
                ...this.subscriptions.filter((s: Subscription<any>) => s.address !== address),
                subscription,
              ];
              setTimeout(() => onSubscription?.(subscription), 0); // don't run right away, not realistic and causes issues
            },
            unregisterSubscription(address) {
              this.subscriptions = this.subscriptions.filter((s: Subscription<any>) => s.address !== address);
            },
            pageSubscription() {
              // TODO
            },
            registerPublication(request, onError?: (err: any) => void) {
              const reqid = this.reqid;
              this.publications.set(reqid, onError);
              this.reqid++;
              return () => {
                this.publications.delete(reqid);
              };
            },
            sendMessage(json: any) {
              if (this.publications.has(json.reqid) && json.type === 'error') {
                this.publications.get(json.reqid)?.(json); // call the onError callback with the error json
                return;
              }

              if (json.type === 'error') {
                const sub = this.subscriptions.find((s: Subscription<any>) => s.reqid === json.reqid);
                // The first argument to Subscription.callback is the error, second is the json
                sub?.callback(json);
              }

              for (const subscription of this.subscriptions) {
                for (const stream of subscription.streams) {
                  const shouldSend = shouldSendToThisStream
                    ? shouldSendToThisStream(json, stream)
                    : stream.name === json.type;
                  if (shouldSend) {
                    subscription.handleJson(json);
                  }
                }
              }
            },
            sendTimedMessage(message): void {
              return this.sendMessage({
                ...message,
                ts: formattedDateForSubscription(new Date()),
              });
            },
            ping(data) {
              return this.sendTimedMessage({
                type: AVA_PING,
                reqid: this.requestCount++,
                data: [data],
              });
            },
            pongs() {
              return EMPTY;
            },
            setBlockedSubscriptions() {},
            performance: null,
            closeAndReconnect() {},
            onOpen: new Subject(),
            onClose: new Subject(),
            onError: new Subject(),
          } as IWebSocketClient<any>,
          status: WebSocketStatus.CONNECTED,
          performance: null,
        } satisfies WebSocketContextProps<any>)
      : (null as unknown)
  ) as WebSocketContextProps<any>;
};

const MOCK_WEBSOCKET_CONTEXT = createMockWebsocketContext() as ContextType<typeof WebSocketContext>;

export const MOCK_USER_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        getOrganization: () =>
          new Promise(r =>
            r({
              ID: 1,
              Name: 'Talos',
              Users: [],
            })
          ),
      }
    : (null as unknown)
) as UserContextProps;

export const MOCK_ENDPOINTS_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        orgApiEndpoint: 'tal-1.talostrading.test:3002',
        wsEndpoint: 'tal-1.talostrading.test:8089',
        apiEndpoint: 'https://test-env.talostrading.com',
      }
    : (null as unknown)
) as EndpointsContextProps;

const MOCK_MARKETS_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        marketsByName: new Map(
          Object.entries({
            binance: { DisplayName: 'Binance' },
            coinbase: { DisplayName: 'Coinbase' },
            kraken: { DisplayName: 'Kraken' },
            enclave: { DisplayName: 'Enclave', Type: 'Dark' },
            one_inch: { DisplayName: '1inch', Type: 'Dealer' },
          })
        ),
        marketsList: [
          { Name: 'binance', DisplayName: 'Binance', Type: 'Dealer' },
          { Name: 'coinbase', DisplayName: 'Coinbase', Type: 'InternalAlgo' },
          { Name: 'kraken', DisplayName: 'Kraken', Type: 'Exchange' },
          { Name: 'enclave', DisplayName: 'Enclave', Type: 'Dark' },
          { Name: 'one_inch', DisplayName: '1inch', Type: 'Dealer' },
        ],
        isMarketConfigured: (...args) => true,
        isMarketOnline: (...args) => true,
      }
    : (null as unknown)
) as MarketsContextProps;

export const MOCK_CURRENCIES_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        currenciesBySymbol: new Map(
          Object.entries({
            USD: {
              Timestamp: '2020-09-17T14:20:48.159024Z',
              Symbol: 'USD',
              MinIncrement: '0.01',
              DefaultIncrement: '0.01',
              Description: 'U.S. Dollar',
            },
            BTC: {
              Timestamp: '2020-06-19T14:31:23.197493Z',
              Symbol: 'BTC',
              MinIncrement: '0.00000001',
              DefaultIncrement: '0.0001',
              Description: 'Bitcoin',
            },
            ETH: {
              Timestamp: '2020-06-19T14:31:23.197493Z',
              Symbol: 'ETH',
              MinIncrement: '0.0000001',
              DefaultIncrement: '0.001',
              Description: 'Ethereum',
            },
          })
        ),
        currenciesList: [
          {
            Timestamp: '2020-09-17T14:20:48.159024Z',
            Symbol: 'USD',
            MinIncrement: '0.01',
            DefaultIncrement: '0.01',
            Description: 'U.S. Dollar',
          },
          {
            Timestamp: '2020-06-19T14:31:23.197493Z',
            Symbol: 'BTC',
            MinIncrement: '0.00000001',
            DefaultIncrement: '0.0001',
            Description: 'Bitcoin',
          },
          {
            Timestamp: '2020-06-19T14:31:23.197493Z',
            Symbol: 'ETH',
            MinIncrement: '0.0000001',
            DefaultIncrement: '0.001',
            Description: 'Ethereum',
          },
        ],
        isLoaded: true,
      }
    : (null as unknown)
) as CurrenciesContextProps;

const BTCUSDSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2021-03-19T11:21:31.746111Z',
        Symbol: 'BTC-USD',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '0.5',
        ProductType: 'Spot',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'BTC-USD',
        Description: 'Bitcoin/U.S. Dollar',
        Markets: ['coinbase', 'kraken', 'flowtraders'],
        Rank: 1,
      }
    : (null as unknown)
) as Security;
const USDEURSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Symbol: 'USD-EUR',
        DisplaySymbol: 'USD-EUR',
        MinPriceIncrement: '0.000001',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.01',
        MaximumSize: '10000000',
        Markets: ['market1', 'market2'],
        BaseCurrency: 'USD',
        QuoteCurrency: 'EUR',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.01',
        SizeBuckets: ['10', '25', '50', '100', '1000'],
        PriceDisplaySpec: 'm.MMMMMm',
        NormalSize: '1000',
        ProductType: 'Spot',
        PositionCurrency: 'USD',
        SettlementCurrency: 'EUR',
        Description: 'Ethereum/Bitcoin',
        SettleValueType: 'Regular',
      }
    : (null as unknown)
) as Security;
const BTCUSDCSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2021-03-21T21:32:29.775797Z',
        Symbol: 'BTC-USDC',
        MinPriceIncrement: '0.00000001',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0',
        MaximumSize: '10000000',
        QuoteCurrency: 'USDC',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '0.5',
        ProductType: 'Spot',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USDC',
        NotionalMultiplier: '1',
        DisplaySymbol: 'BTC-USDC',
        Description: 'Bitcoin/USD Coin',
        Markets: ['coinbase', 'binance', 'kraken', 'poloniex', 'bitstamp', 'alameda', 'b2c2', 'ftx-otc', 'binance_us'],
        Rank: 5,
      }
    : (null as unknown)
) as Security;

const BTCUSDTSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2021-03-21T21:32:29.775797Z',
        Symbol: 'BTC-USDT',
        MinPriceIncrement: '0.00000001',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0',
        MaximumSize: '10000000',
        QuoteCurrency: 'USDT',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '0.5',
        ProductType: 'Spot',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USDT',
        NotionalMultiplier: '1',
        DisplaySymbol: 'BTC-USDT',
        Description: 'Bitcoin/Tether USD',
        Markets: [
          'binance',
          'kraken',
          'poloniex',
          'bittrex',
          'alameda',
          'cumberland',
          'b2c2',
          'galaxy',
          'ftx-otc',
          'binance_us',
        ],
        Rank: 15,
      }
    : (null as unknown)
) as Security;

const ETHUSDSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2021-03-19T11:21:31.746111Z',
        Symbol: 'ETH-USD',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.0000001',
        MinimumSize: '0',
        MaximumSize: '1000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'ETH',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '0.5',
        ProductType: 'Spot',
        PositionCurrency: 'ETH',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'ETH-USD',
        Description: 'Ethereum/U.S. Dollar',
        Markets: ['coinbase', 'kraken', 'flowtraders'],
        Rank: 2,
      }
    : (null as unknown)
) as Security;

const ETHBTCSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2021-03-19T11:21:31.746111Z',
        Symbol: 'ETH-BTC',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.0000001',
        MinimumSize: '0',
        MaximumSize: '1000000',
        QuoteCurrency: 'BTC',
        BaseCurrency: 'ETH',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '0.5',
        ProductType: 'Spot',
        PositionCurrency: 'ETH',
        SettlementCurrency: 'BTC',
        NotionalMultiplier: '1',
        DisplaySymbol: 'ETH-BTC',
        Description: 'Ethereum/Bitcoin',
        Markets: ['coinbase', 'kraken', 'flowtraders'],
        Rank: 10,
      }
    : (null as unknown)
) as Security;

const BTCUSDPERPSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2023-05-15T10:31:00.798474Z',
        Symbol: 'binance_futures:BTCUSD-PERP',
        MinPriceIncrement: '0.1',
        MinSizeIncrement: '1',
        MinimumSize: '1',
        MaximumSize: '0',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.1',
        DefaultSizeIncrement: '1',
        PriceDisplaySpec: 'M.Mm',
        SizeDisplaySpec: 'M.m',
        NormalSize: '100',
        ProductType: 'PerpetualSwap',
        SettlementCurrency: 'BTC',
        NotionalMultiplier: '100',
        DisplaySymbol: 'BINANCE BTCUSD Perp',
        Description: 'BINANCE BTCUSD Coin Perpetual Swap',
        SizeBuckets: ['100', '250', '500', '1000', '2000'],
        Markets: ['binance_futures'],
        SettleValueType: 'Inverted',
        Rank: 7,
      }
    : (null as unknown)
) as Security;

const BTCUSDTPERPSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2023-05-15T10:31:00.798474Z',
        Symbol: 'binance_futures:BTCUSDT-PERP',
        MinPriceIncrement: '0.1',
        MinSizeIncrement: '0.001',
        MinimumSize: '0.001',
        MaximumSize: '0',
        QuoteCurrency: 'USDT',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.1',
        DefaultSizeIncrement: '0.001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'PerpetualSwap',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USDT',
        NotionalMultiplier: '1',
        DisplaySymbol: 'BINANCE BTCUSDT Perp',
        Description: 'BINANCE BTCUSDT Perpetual Swap',
        SizeBuckets: ['1', '5', '10', '50', '100'],
        Markets: ['binance_futures'],
        SettleValueType: 'Regular',
        Rank: 1,
      }
    : (null as unknown)
) as Security;

const okxBTCUSDTPERPSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2023-05-15T10:31:00.798474Z',
        Symbol: 'okx:BTCUSDT-PERP',
        MinPriceIncrement: '0.1',
        MinSizeIncrement: '1',
        MinimumSize: '1',
        MaximumSize: '0',
        QuoteCurrency: 'USDT',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.1',
        DefaultSizeIncrement: '1',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '10',
        ProductType: 'PerpetualSwap',
        SettlementCurrency: 'USDT',
        NotionalMultiplier: '0.01',
        DisplaySymbol: 'OKX BTC-USDT-SWAP Perp',
        Description: 'OKX BTC-USDT-SWAP Perpetual Swap',
        SizeBuckets: ['10', '25', '50', '100', '200'],
        Markets: ['okex'],
        SettleValueType: 'Regular',
        Rank: 5,
      }
    : (null as unknown)
) as Security;

const okxBTCUSDPERPSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2024-10-14T23:39:08.187524Z',
        Symbol: 'okx:BTCUSD-PERP',
        MinPriceIncrement: '0.1',
        MinSizeIncrement: '1',
        MinAmtIncrement: '0',
        MinimumSize: '1',
        MaximumSize: '0',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.1',
        DefaultSizeIncrement: '1',
        PriceDisplaySpec: 'M.Mm',
        SizeDisplaySpec: 'M.m',
        NormalSize: '100',
        ProductType: 'PerpetualSwap',
        SettlementCurrency: 'BTC',
        NotionalMultiplier: '100',
        DisplaySymbol: 'OKX BTC-USD-SWAP Perp',
        Description: 'OKX BTC-USD-SWAP Coin Perpetual Swap',
        SizeBuckets: ['100', '250', '500', '1000', '2000'],
        Markets: ['okex'],
        SettleValueType: 'Inverted',
        Rank: 18,
      }
    : (null as unknown)
) as Security;

const ETHBTCviaUSDSecurity = (
  envConfig.VITE_AVA_ENV === 'test'
    ? {
        Timestamp: '2022-12-01T19:50:22.621473Z',
        Symbol: 'ETH-BTC:USD',
        MinPriceIncrement: '0.00000001',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '100000000',
        QuoteCurrency: 'BTC',
        BaseCurrency: 'ETH',
        DefaultPriceIncrement: '0.00001',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.MMMM',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'Synthetic',
        PositionCurrency: 'ETH',
        SettlementCurrency: 'BTC',
        NotionalMultiplier: '1',
        Composition: 'Synthetic',
        StrikePrice: '0',
        DisplaySymbol: 'ETH-BTC via USD',
        Description: 'ETH-BTC via USD',
        SizeBuckets: ['2', '5', '10', '20', '40'],
        Markets: ['multileg'],
        SettleValueType: 'Regular',
        Rank: 1,
        MultilegDetails: {
          FeeMode: 'Net',
          Legs: [
            {
              Category: '',
              Direction: 'Same',
              Index: 0,
              Markets: [
                {
                  Market: 'coinbase',
                  MarketAccount: 'coinbase/coinbase',
                },
                {
                  Market: 'kraken',
                  MarketAccount: 'kraken/kraken',
                },
                {
                  Market: 'b2c2',
                  MarketAccount: 'b2c2/b2c2',
                },
                {
                  Market: 'cumberland',
                  MarketAccount: 'cumberland/cumberland',
                },
                {
                  Market: 'galaxy',
                  MarketAccount: 'galaxy/galaxy',
                },
                {
                  Market: 'dvchain',
                  MarketAccount: 'dvchain/dvchain',
                },
                {
                  Market: 'osl_am',
                  MarketAccount: 'osl_am/osl_am',
                },
              ],
              Symbol: 'ETH-USD',
            },
            {
              Category: '',
              Direction: 'Opposite',
              Index: 1,
              Markets: [
                {
                  Market: 'coinbase',
                  MarketAccount: 'coinbase/coinbase',
                },
                {
                  Market: 'kraken',
                  MarketAccount: 'kraken/kraken',
                },
                {
                  Market: 'b2c2',
                  MarketAccount: 'b2c2/b2c2',
                },
                {
                  Market: 'cumberland',
                  MarketAccount: 'cumberland/cumberland',
                },
                {
                  Market: 'galaxy',
                  MarketAccount: 'galaxy/galaxy',
                },
                {
                  Market: 'dvchain',
                  MarketAccount: 'dvchain/dvchain',
                },
                {
                  Market: 'osl_am',
                  MarketAccount: 'osl_am/osl_am',
                },
              ],
              Symbol: 'BTC-USD',
            },
          ],
          MarketDataDepth: 20,
          Parameters: {
            LegParams: [
              {
                Initiating: true,
                HaircutRatio: '0',
              },
              {
                Initiating: true,
                HaircutRatio: '0',
              },
            ],
          },
          ProductType: 'Spot',
        },
      }
    : (null as unknown)
) as Security;

const OptionSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2022-12-15T02:15:39.074880Z',
        Symbol: 'deribit:BTC-31MAR23-25000-P',
        MinPriceIncrement: '0.0005',
        MinSizeIncrement: '0.1',
        MinimumSize: '0.1',
        MaximumSize: '0',
        QuoteCurrency: 'BTC',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0',
        DefaultSizeIncrement: '0',
        PriceDisplaySpec: 'M.MMMm',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'Option',
        SettlementCurrency: 'BTC',
        NotionalMultiplier: '1',
        Expiration: '2022-03-31T08:00:00.000000Z',
        OptionType: 'Put',
        StrikePrice: '25000',
        UnderlyingSymbol: 'deribit:BTC-31MAR23',
        DisplaySymbol: 'Deribit BTC-31MAR23-25000-P',
        Description: 'Deribit BTC Put Option 31MAR23 25000',
        SizeBuckets: ['1', '2', '5', '10', '50'],
        Markets: ['deribit'],
        SettleValueType: 'Regular',
      }
    : (null as unknown)
) as Security;

const CalendarSpreadSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2023-11-02T15:54:01.563775Z',
        Symbol: 'cme:BTCX3-BTCZ3_20231124',
        MinPriceIncrement: '1',
        MinSizeIncrement: '1',
        MinAmtIncrement: '0',
        MinimumSize: '1',
        MaximumSize: '0',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '1',
        DefaultSizeIncrement: '1',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'CalendarSpread',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '5',
        Expiration: '2023-11-24T16:00:00.000000Z',
        DisplaySymbol: 'BTCX3-BTCZ3',
        Description: 'CME BTC-USD 231124 Futures',
        SizeBuckets: ['1', '2', '5', '10', '50'],
        Markets: ['cme'],
        SettleValueType: 'Regular',
        Legs: [
          {
            Symbol: 'cme:BTCX3_20231124',
            Direction: 'Same',
          },
          {
            Symbol: 'cme:BTCZ3_20231229',
            Direction: 'Same',
          },
        ],
      }
    : (null as unknown)
) as Security;

// OTC Derivatives Securities (FWD prefix)
const fwdBtcForwardSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2024-01-15T10:30:00Z',
        Symbol: 'FWD-BTC-25MAR25',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'OTC',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'FWD-BTC-25MAR25',
        Description: 'Bitcoin Forward March 2025',
        Markets: ['otc'],
        Rank: 1,
      }
    : (null as unknown)
) as Security;

const fwdEthForwardSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2024-01-16T14:45:00Z',
        SubmitTime: '2024-01-16T14:45:00Z',
        Symbol: 'FWD-ETH-30JUN25',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'ETH',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'OTC',
        PositionCurrency: 'ETH',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'FWD-ETH-30JUN25',
        Description: 'Ethereum Forward June 2025',
        Markets: ['otc'],
        Rank: 2,
      }
    : (null as unknown)
) as Security;

const fwdSolForwardSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2024-01-17T09:15:00Z',
        Symbol: 'FWD-SOL-31DEC25',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'SOL',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'OTC',
        PositionCurrency: 'SOL',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'FWD-SOL-31DEC25',
        Description: 'Solana Forward December 2025',
        Markets: ['otc'],
        Rank: 3,
      }
    : (null as unknown)
) as Security;

// Loan Securities (LOAN prefix)
const loanBtcFixedSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? ({
        SubProductType: SubProductTypeEnum.OTCFixedRateLoan,
        Timestamp: '2024-01-15T10:30:00Z',
        Symbol: 'LOAN-BTC-1Y-FIXED',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'OTC',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'LOAN-BTC-1Y-FIXED',
        Description: 'Bitcoin 1 Year Fixed Rate Loan',
        Markets: ['otc'],
        Rank: 4,
        ProductDetails: {
          Term: '1Y',
          BorrowedAsset: 'BTC',
        },
      } satisfies OTCFixedRateLoanProductTemplate)
    : (null as unknown)
) as Security;

const loanEthFloatingSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? ({
        SubProductType: SubProductTypeEnum.OTCFloatingRateLoan,
        Timestamp: '2024-01-16T14:45:00Z',
        Symbol: 'LOAN-ETH-6M-FLOATING',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.00000001',
        MinimumSize: '0.00000001',
        MaximumSize: '10000000',
        QuoteCurrency: 'USD',
        BaseCurrency: 'ETH',
        DefaultPriceIncrement: '0.01',
        DefaultSizeIncrement: '0.0001',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'OTC',
        PositionCurrency: 'ETH',
        SettlementCurrency: 'USD',
        NotionalMultiplier: '1',
        DisplaySymbol: 'LOAN-ETH-6M-FLOATING',
        Description: 'Ethereum 6 Month Floating Rate Loan',
        Markets: ['otc'],
        Rank: 5,
        ProductDetails: {
          Term: '6M',
          BorrowedAsset: 'ETH',
        },
      } satisfies OTCFloatingRateLoanProductTemplate)
    : (null as unknown)
) as Security;

const multilegSameMarketSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2023-11-07T11:27:37.017471Z',
        Symbol: 'BTC-USDT|bybit:BTCUSDT-PERP-2',
        MinPriceIncrement: '0.1',
        MinSizeIncrement: '0.001000000000',
        MinAmtIncrement: '0',
        MinimumSize: '0.001000000000',
        MaximumSize: '100000000',
        QuoteCurrency: 'USDT',
        BaseCurrency: 'BTC',
        DefaultPriceIncrement: '0.1',
        DefaultSizeIncrement: '0.001000000000',
        PriceDisplaySpec: 'M.m',
        SizeDisplaySpec: 'M.m',
        NormalSize: '1',
        ProductType: 'Synthetic',
        PositionCurrency: 'BTC',
        SettlementCurrency: 'USDT',
        NotionalMultiplier: '1',
        Composition: 'Synthetic',
        StrikePrice: '0',
        DisplaySymbol: 'BTC-USDT v BTCUSDT.P BB 2',
        Description: 'BTC-USDT vs BYBIT BTCUSDT Perp',
        SizeBuckets: ['1', '5', '10', '50', '100'],
        Markets: ['multileg'],
        SettleValueType: 'Regular',
        Rank: 1,
        MultilegDetails: {
          FeeMode: 'Net',
          Legs: [
            {
              Category: '',
              Direction: 'Same',
              Index: 0,
              Markets: [
                {
                  Market: 'bybit',
                  MarketAccount: 'bybit/by-bit',
                },
              ],
              Symbol: 'BTC-USDT',
            },
            {
              Category: '',
              Direction: 'Opposite',
              Index: 1,
              Markets: [
                {
                  Market: 'bybit',
                  MarketAccount: 'bybit/by-bit',
                },
              ],
              Symbol: 'bybit:BTCUSDT-PERP',
            },
          ],
          MarketDataDepth: 20,
          Parameters: {
            LegParams: [
              {
                CrossingAllowed: true,
                Initiating: true,
                MaxRestingLevels: 1,
                HaircutRatio: '0',
              },
              {
                CrossingAllowed: true,
                Initiating: true,
                MaxRestingLevels: 1,
                HaircutRatio: '0',
              },
            ],
          },
          ProductType: 'Synthetic',
          SyntheticProductType: 'Delta1Spread',
        },
      }
    : (null as unknown)
) as Security;

const GDLCSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Composition: 'Native',
        Description: 'GDLC 2BTC/3ETH compound',
        DisplaySymbol: 'GDLC 2BTC/3ETH',
        Legs: [
          {
            Direction: 'Same',
            Symbol: 'BTC-USD',
          },
          {
            Direction: 'Same',
            Symbol: 'ETH-USD',
          },
        ],
        Markets: ['cumberland', 'enigma'],
        DefaultPriceIncrement: '0.01',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '0.01',
        ProductType: 'Compound',
        QuoteCurrency: 'USD',
        SettleValueType: 'Regular',
        SettlementCurrency: 'USD',
        SizeBuckets: [],
        Symbol: 'GDLC',
        Timestamp: '2025-10-27T22:46:18.989778Z',
      }
    : (null as unknown)
) as Security;

const F1_2026_WDC_EVENT_ID = '1SBV6CJD80C00';

const norrisOutcomeSecurity = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        Timestamp: '2026-06-12T06:08:11.183074Z',
        Symbol: 'F1-2026-WDC-NORRIS',
        DisplaySymbol: 'Lando Norris',
        Description: 'Resolves YES if Lando Norris wins the 2026 F1 WDC',
        Composition: 'Native',
        ProductType: 'PredictionOutcome',
        QuoteCurrency: 'USD',
        SettlementCurrency: 'USD',
        SettleValueType: 'Regular',
        MinPriceIncrement: '0.01',
        MinSizeIncrement: '1',
        MinimumSize: '0',
        MaximumSize: '0',
        NotionalMultiplier: '1',
        PriceNormalizationFactor: '1',
        Markets: ['kalshi'],
        SizeBuckets: ['1'],
        // Matches Ava `F1_2026_WDC_EVENT.EventID`.
        PredictionOutcomeEventID: F1_2026_WDC_EVENT_ID,
      }
    : (null as unknown)
) as Security;

function createMockSecuritiesMaps() {
  const securitiesBySymbol = new Map(
    Object.entries({
      'BTC-USD': BTCUSDSecurity,
      'BTC-USDC': BTCUSDCSecurity,
      'BTC-USDT': BTCUSDTSecurity,
      'ETH-USD': ETHUSDSecurity,
      'ETH-BTC': ETHBTCSecurity,
      'binance_futures:BTCUSD-PERP': BTCUSDPERPSecurity, // Inverted (can be traded in contracts)
      'binance_futures:BTCUSDT-PERP': BTCUSDTPERPSecurity, // Regular (cannot be traded in contracts)
      'okx:BTCUSDT-PERP': okxBTCUSDTPERPSecurity, // Regular (can be traded in contracts)
      'okx:BTCUSD-PERP': okxBTCUSDPERPSecurity, // Inverted (can be traded in contracts)
      'ETH-BTC:USD': ETHBTCviaUSDSecurity,
      'deribit:BTC-31MAR23-25000-P': OptionSecurity,
      'cme:BTCX3-BTCZ3_20231124': CalendarSpreadSecurity,
      'BTC-USDT|bybit:BTCUSDT-PERP-2': multilegSameMarketSecurity,
      'USD-EUR': USDEURSecurity,
      // OTC Derivatives symbols (should start with FWD)
      'FWD-BTC-25MAR25': fwdBtcForwardSecurity,
      'FWD-ETH-30JUN25': fwdEthForwardSecurity,
      'FWD-SOL-31DEC25': fwdSolForwardSecurity,
      // Loan symbols (should start with LOAN)
      'LOAN-BTC-1Y-FIXED': loanBtcFixedSecurity,
      'LOAN-ETH-6M-FLOATING': loanEthFloatingSecurity,
      GDLC: GDLCSecurity,
      'F1-2026-WDC-NORRIS': norrisOutcomeSecurity,
    })
  );

  return {
    securitiesBySymbol,
    activeSecuritiesBySymbol: new Map(
      [...securitiesBySymbol].filter(([, security]) => security != null && isActiveSecurity(security))
    ),
  };
}

export const MOCK_SECURITIES_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        ...createMockSecuritiesMaps(),
        searchableSecurities: [
          BTCUSDSecurity,
          ETHUSDSecurity,
          ETHBTCSecurity,
          BTCUSDTSecurity,
          BTCUSDCSecurity,
          USDEURSecurity,
        ],
        securitiesList: [
          BTCUSDSecurity,
          ETHUSDSecurity,
          ETHBTCSecurity,
          BTCUSDTSecurity,
          BTCUSDCSecurity,
          USDEURSecurity,
        ],
        securitiesListSortedByRank: [
          BTCUSDSecurity,
          ETHUSDSecurity,
          ETHBTCSecurity,
          BTCUSDTSecurity,
          BTCUSDCSecurity,
          USDEURSecurity,
        ].toSorted((a, b) => ((a.Rank ?? Infinity) < (b.Rank ?? Infinity) ? -1 : 1)),
        predictionOutcomesBySymbol: new Map([[norrisOutcomeSecurity.Symbol, norrisOutcomeSecurity]]),
        predictionOutcomesByEventID: new Map([
          [F1_2026_WDC_EVENT_ID, new Map([[norrisOutcomeSecurity.Symbol, norrisOutcomeSecurity]])],
        ]),
        predictionSupportedMarkets: ['kalshi'],
        cfds: [],
        isLoaded: true,
      }
    : (null as unknown)
) as SecuritiesContextProps;

const STRATEGIES_LIST_RAW = [
  {
    Name: 'Limit',
    Group: 'All',
    Parameters: [
      {
        Name: 'EndTime',
        Type: 'Date',
        Presence: 'Optional',
        Description: 'Expire time for the order.',
        DisplayName: 'End Time',
      },
    ],
    Description:
      'For marketable orders, the algorithm will sweep the available liquidity up to the limit price by routing to those market(s) offering the best prices. The remaining quantity will be split equally among the selected market(s) at the limit price.',
    DisplayName: 'Limit',
  },
  {
    Name: 'MultilegLimit',
    Group: 'All',
    AlgoType: 'Base',
    InstrumentScope: 'Synthetic',
    Parameters: [
      {
        Name: 'StartTime',
        Type: 'Date',
        Presence: 'Optional',
        Description: 'Time at which this order will activate and begin sending orders to the market.',
        DisplayName: 'Start Time',
      },
      {
        Name: 'EndTime',
        Type: 'Date',
        Presence: 'Optional',
        Description: 'Expire time for the order.',
        DisplayName: 'End Time',
      },
    ],
    Description:
      'For marketable orders, the algorithm will sweep the available liquidity up to the limit price by routing to those market(s) offering the best prices. The remaining quantity will be split equally among the selected market(s) at the limit price.',
    DisplayName: 'Limit',
  },
  {
    Name: 'TWAP',
    Group: 'Exchange',
    Parameters: [
      {
        Name: 'EndTime',
        Type: 'Date',
        Presence: 'Required',
        Description: 'Expire time for the order.',
        DisplayName: 'End Time',
      },
    ],
    Description:
      'Time-Weighted Average Price algorithm that trades over a specified duration of time, breaking up the order into smaller parts. The algorithm is geared to minimize market impact and reduce trading fees.',
    DisplayName: 'TWAP',
  },
  {
    Name: 'MultilegTWAP',
    Group: 'Exchange',
    AlgoType: 'TWAP',
    InstrumentScope: 'Synthetic',
    StrategyScope: 'OrderStrategy',
    Parameters: [
      {
        Name: 'EndTime',
        Type: 'Date',
        Presence: 'Required',
        Description: 'Expire time for the order.',
        DisplayName: 'End Time',
      },
      {
        Name: 'StartTime',
        Type: 'Date',
        Presence: 'Optional',
        Description: 'Time at which this order will activate and begin sending orders to the market.',
        DisplayName: 'Start Time',
      },
      {
        Name: 'BoundsControl',
        Type: 'Enum',
        Presence: 'Optional',
        EnumValues: [
          { Index: 1, Name: 'Narrow', Description: 'Keep the order close to the ideal TWAP fill progression' },
          {
            Index: 2,
            Name: 'Standard',
            Description:
              'The order may diverge a reasonable amount from the ideal TWAP fill progression, to account for market conditions',
          },
          {
            Index: 3,
            Name: 'Wide',
            Description:
              'The order may diverge a large amount from the ideal TWAP fill progression, to account for market conditions',
          },
        ],
        Description: 'Selection of how strictly the TWAP algo stays with the target fill progression',
        DisplayName: 'Bounds Control',
        DefaultValue: '2',
      },
      {
        Name: 'AlgoInstructions',
        Type: 'JSON',
        Presence: 'Hidden',
        Description: 'AlgoInstructions',
        DisplayName: 'AlgoInstructions',
        DefaultValue:
          '{"LegCrossingToInitiate":true,"LegRestingToInitiate":true,"LegHedgeAggressiveAtMarket":true,"LegHedgePassivePegged":true}',
      },
    ],
    Description:
      'Time-Weighted Average Price algorithm that trades over a specified duration of time, breaking up the order into smaller parts. The algorithm is geared to minimize market impact and reduce trading fees.',
    DisplayName: 'Multileg TWAP',
  },
  {
    // Dark Pool strategy
    Name: 'EnclaveCross',
    Group: 'Dark',
    DisplayName: 'Enclave Cross',
  },
  {
    Name: 'Scaled',
    Group: 'All',
    DisplayName: 'Scaled',
    Metadata: `{ "PriceIntervalEnd": "ScaleEndPrice" }`,
    Parameters: [
      {
        Name: 'ScaleEndPrice',
        Type: 'Price',
        Presence: 'Required',
        DisplayName: 'Scale End Price',
      },
    ],
  },
  {
    Name: 'CannotModify',
    Group: 'All',
    DisplayName: 'Cannot Modify',
    Metadata: { CanBeModified: false },
  },
];
const STRATEGIES_LIST = STRATEGIES_LIST_RAW.map(strategy => new OrderStrategy(strategy as JSON_OrderStrategy));
const STRATEGIES_MAP = new Map(STRATEGIES_LIST.map(strategy => [strategy.Name, strategy]));

const USERS = [
  {
    ID: '000',
    Name: 'I am the boss',
    Email: 'boss@talos.com',
    Roles: ['talos.admin'],
  },
  {
    ID: '007',
    Name: 'James Bond',
    Email: 'james@talos.com',
    Roles: ['org.viewer'],
  },
];

export const MOCK_STRATEGIES_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? {
        strategiesByNameObs: of(STRATEGIES_MAP),
        strategiesByName: STRATEGIES_MAP,
        strategiesList: [...STRATEGIES_MAP.values()],
        listStrategiesObs: of([...STRATEGIES_MAP.values()]),
        isLoaded: true,
      }
    : (null as unknown)
) as StrategiesContextProps;

const MOCK_BLOTTER_TABLE_CONTEXT = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT
    ? ({
        currenciesBySymbol: MOCK_CURRENCIES_CONTEXT.currenciesBySymbol,
        securitiesBySymbol: MOCK_SECURITIES_CONTEXT.securitiesBySymbol,
        strategies: MOCK_STRATEGIES_CONTEXT.strategiesByNameObs,
        strategiesByName: MOCK_STRATEGIES_CONTEXT.strategiesByName,
        userByID: new Map(USERS.map(user => [user.ID, user])),
        userByEmail: new Map(USERS.map(user => [user.Email, user])),
        currentUser: USERS[1], // non-Talos user
        intl: intlDefaultFormatter,
      } as unknown)
    : (null as unknown)
) as BlotterTableContextProps;

const MOCK_HOME_CURRENCY = (
  import.meta.env.VITE_IS_TEST_ENVIRONMENT ? { homeCurrency: 'USD' } : (null as unknown)
) as HomeCurrencyContextProps;

/**
 * Full app context provider for testing - best used for vitest unit tests
 * - For Cypress testing, MockProviders is often a better fit, since it supports routing behavior
 */
export const TestProviders = (props: PropsWithChildren) => {
  if (import.meta.env.VITE_IS_TEST_ENVIRONMENT) {
    return (
      <MixpanelContext.Provider value={GET_MIXPANEL_MOCK}>
        <MixpanelSourceContext.Provider value={'test' as any}>
          <UserContext.Provider value={MOCK_USER_CONTEXT}>
            <GlobalDialogProvider>
              <EndpointsContext.Provider value={MOCK_ENDPOINTS_CONTEXT}>
                <WebSocketContext.Provider value={MOCK_WEBSOCKET_CONTEXT}>
                  <MarketsContext.Provider value={MOCK_MARKETS_CONTEXT}>
                    <CurrenciesContext.Provider value={MOCK_CURRENCIES_CONTEXT}>
                      <SecuritiesContext.Provider value={MOCK_SECURITIES_CONTEXT}>
                        <StrategiesContext.Provider value={MOCK_STRATEGIES_CONTEXT}>
                          <HomeCurrencyContext.Provider value={MOCK_HOME_CURRENCY}>
                            <BlotterTableContext.Provider value={MOCK_BLOTTER_TABLE_CONTEXT}>
                              <ThemeContext.Provider value={DarkTheme}>
                                <PortalContextProvider>
                                  <AppwideDrawerProviderWithLocalGuideState>
                                    <IntlProvider locale={DEFAULT_LOCALE}>{props.children}</IntlProvider>
                                  </AppwideDrawerProviderWithLocalGuideState>
                                </PortalContextProvider>
                              </ThemeContext.Provider>
                            </BlotterTableContext.Provider>
                          </HomeCurrencyContext.Provider>
                        </StrategiesContext.Provider>
                      </SecuritiesContext.Provider>
                    </CurrenciesContext.Provider>
                  </MarketsContext.Provider>
                </WebSocketContext.Provider>
              </EndpointsContext.Provider>
            </GlobalDialogProvider>
          </UserContext.Provider>
        </MixpanelSourceContext.Provider>
      </MixpanelContext.Provider>
    );
  }
  return null;
};
